import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';

function Cierre() {
  const [planes, setPlanes] = useState([]);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [diferencias, setDiferencias] = useState([]);
  const [preciosEditados, setPreciosEditados] = useState({});
  const [mensaje, setMensaje] = useState('');
  const [nroCompra, setNroCompra] = useState('');
  const [vistaActiva, setVistaActiva] = useState('resumen');
  const [avisados, setAvisados] = useState({});
  const [observaciones, setObservaciones] = useState({});
  const [productosNuevos, setProductosNuevos] = useState([]);
  const [subiendoUnica, setSubiendoUnica] = useState(false);
  const [unicaCargada, setUnicaCargada] = useState(false);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('token');

  const cargarPlanes = async () => {
    try {
      const res = await axios.get('https://comprasmercadomodelo.online/api/cierre/planes', { headers: { authorization: token } });
      setPlanes(res.data);
    } catch (err) { console.error(err); }
  };

  const cargarResumen = async (plan_id) => {
    try {
      const [resRes, difRes] = await Promise.all([
        axios.get(`https://comprasmercadomodelo.online/api/cierre/planes/${plan_id}/resumen`, { headers: { authorization: token } }),
        axios.get(`https://comprasmercadomodelo.online/api/cierre/planes/${plan_id}/diferencias`, { headers: { authorization: token } })
      ]);
      setPedidos(resRes.data);
      setDiferencias(difRes.data);
      setPlanSeleccionado(plan_id);
      const avisadosInit = {};
      const obsInit = {};
      difRes.data.forEach(d => {
        avisadosInit[d.id] = d.avisado_proveedor || false;
        obsInit[d.id] = d.observacion_diferencia || '';
      });
      setAvisados(avisadosInit);
      setObservaciones(obsInit);
    } catch (err) { console.error(err); }
  };

  const cargarProductosNuevos = async (plan_id) => {
    try {
      const res = await axios.get(`https://comprasmercadomodelo.online/api/unica/nuevos/${plan_id}`, { headers: { authorization: token } });
      setProductosNuevos(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { cargarPlanes(); }, []);

  useEffect(() => {
    if (!planSeleccionado) return;
    const planActual = planes.find(p => p.id === planSeleccionado);
    if (planActual?.estado === 'cerrado') return;
    const interval = setInterval(() => cargarResumen(planSeleccionado), 30000);
    return () => clearInterval(interval);
  }, [planSeleccionado, planes]);

  useEffect(() => {
    if (planSeleccionado && unicaCargada) cargarProductosNuevos(planSeleccionado);
  }, [planSeleccionado, unicaCargada]);

  const subirListaUnica = async (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;
    setSubiendoUnica(true);
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      const res = await axios.post('https://comprasmercadomodelo.online/api/unica/subir', formData, {
        headers: { authorization: token, 'Content-Type': 'multipart/form-data' }
      });
      setUnicaCargada(true);
      mostrarMensaje(`✅ Lista única cargada — ${res.data.total} productos`);
      if (planSeleccionado) cargarProductosNuevos(planSeleccionado);
    } catch (err) { mostrarMensaje('❌ Error al cargar la lista'); }
    setSubiendoUnica(false);
  };

  const actualizarPrecio = async (id) => {
    const precio = preciosEditados[id];
    if (!precio) return;
    try {
      await axios.put(`https://comprasmercadomodelo.online/api/cierre/pedidos/${id}/precio`, { precio }, { headers: { authorization: token } });
      setMensaje('✅ Precio actualizado');
      setPreciosEditados({});
      cargarResumen(planSeleccionado);
    } catch (err) { console.error(err); }
  };

  const guardarDiferencia = async (id) => {
    try {
      await axios.put(`https://comprasmercadomodelo.online/api/cierre/pedidos/${id}/diferencia`,
        { avisado_proveedor: avisados[id] ? 1 : 0, observacion_diferencia: observaciones[id] || '' },
        { headers: { authorization: token } }
      );
      setMensaje('✅ Guardado correctamente');
    } catch (err) { console.error(err); }
  };

  const exportarCSV = async () => {
    try {
      const res = await axios.get(`https://comprasmercadomodelo.online/api/cierre/planes/${planSeleccionado}/csv`, { headers: { authorization: token } });
      const datos = res.data;
      const sep = ';';
      const headers = ['NRO_COMPRA', 'PROVEEDOR', 'COD_PROVEEDOR', 'PRODUCTO', 'COD_PRODUCTO', 'CODIGO', 'CARGADO', 'CANTIDAD', 'PRECIO', 'SUBTOTAL', 'VACIOS', 'PRECIO_VACIOS', 'KILOS', 'BULTOS_CARGADOS', 'ESTADO_CARGA', 'ESTADO_DESCARGA', 'OBSERVACION'];
      const filas = datos.map(d => [
        nroCompra || planSeleccionado,
        d.proveedor, d.cod_proveedor, d.producto, d.cod_producto,
        d.codigo, d.cargado || 0, d.cantidad || 0, d.precio || 0,
        d.subtotal || 0, d.vacios || 0, d.precio_vacios || 0,
        d.kilos || 0, d.bultos_cargados || 0,
        d.estado_carga || '', d.estado_descarga || '', d.observacion || ''
      ]);
      const csvContent = [headers, ...filas].map(f => f.join(sep)).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `compra_plan_${planSeleccionado}.csv`;
      link.click();
      setMensaje('✅ CSV exportado correctamente');
    } catch (err) { console.error(err); }
  };

  const exportarPlanillaDescarga = async () => {
    const filas = [...pedidos].sort((a, b) => a.producto_nombre.localeCompare(b.producto_nombre));
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Planilla");

    ws.columns = [
      { width: 10.57 }, { width: 41.43 }, { width: 13.43 }, { width: 8.57 },
      { width: 10.57 }, { width: 11.43 }, { width: 26.14 }, { width: 25.29 }
    ];

    ws.mergeCells("A1:G1");
    const fechaCell = ws.getCell("A1");
    fechaCell.value = "FECHA:..........................................";
    fechaCell.font = { bold: true, size: 12, name: "Calibri" };
    ws.getRow(1).height = 39;

    const headerRow = ws.addRow(["CODIGO", "PRODUCTO", "CANTIDAD", "VACIO", "$ VACIO", "REAL", "KG", "PROVEEDOR"]);
    headerRow.height = 27.95;
    const medium = { style: "medium" };
    const thin = { style: "thin" };
    const headerBorders = [
      { top: medium, bottom: medium, left: medium },
      { top: medium, bottom: medium, left: medium, right: medium },
      { top: medium, bottom: medium, left: medium, right: medium },
      { top: medium, bottom: medium, left: medium, right: medium },
      { top: medium, bottom: medium, left: medium },
      { top: medium, bottom: medium, left: medium, right: medium },
      { top: medium, bottom: medium, right: medium },
      {}
    ];
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 14, name: "Calibri" };
      cell.alignment = { horizontal: colNumber === 8 ? "left" : "center" };
      cell.border = headerBorders[colNumber - 1];
    });

    const dataBorders = [
      { bottom: thin, left: medium },
      { bottom: thin, left: medium, right: medium },
      { bottom: thin, left: medium, right: medium },
      { bottom: thin, left: medium, right: medium },
      { bottom: thin, right: medium },
      { bottom: thin, left: medium, right: medium },
      { bottom: thin, right: medium },
      {}
    ];
    const dataFonts = [
      { size: 12, name: "Calibri" },
      { size: 18, name: "Calibri" },
      { size: 18, name: "Calibri", bold: true },
      { size: 12, name: "Calibri" },
      { size: 12, name: "Calibri" },
      { size: 12, name: "Calibri" },
      { size: 11, name: "Calibri" },
      { size: 8, name: "Calibri" }
    ];
    const dataAligns = ["center", "left", "center", "center", "center", "center", "center", "left"];

    filas.forEach(p => {
      const row = ws.addRow([
        p.producto_codigo || "",
        p.producto_nombre || "",
        Number(p.cantidad_pedida) || 0,
        (p.vacio_id ? (Number(p.cantidad_vacios) || Number(p.cantidad_pedida) || 0) : 0),
        Number(p.precio_vacio) || 0,
        "",
        "",
        p.proveedor_nombre || ""
      ]);
      row.height = 27.95;
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.font = dataFonts[colNumber - 1];
        cell.alignment = { horizontal: dataAligns[colNumber - 1] };
        cell.border = dataBorders[colNumber - 1];
      });
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `Planilla_Descarga_Plan_${planSeleccionado}.xlsx`);
  };
  const cerrarCompra = async () => {
    if (!window.confirm('¿Cerrar definitivamente esta compra?')) return;
    try {
      await axios.put(`https://comprasmercadomodelo.online/api/cierre/planes/${planSeleccionado}/cerrar`, {}, { headers: { authorization: token } });
      setMensaje('✅ Compra cerrada correctamente');
      cargarPlanes();
      cargarResumen(planSeleccionado);
    } catch (err) { console.error(err); }
  };

  const copiarListaUnica = () => {
    const texto = productosNuevos.map(p =>
      `${p.codigo_barras}\t${p.nombre.toLowerCase()}\t\t\t${Math.round(Number(p.costo))}`
    ).join('\n');
    navigator.clipboard.writeText(texto).then(() => {
      mostrarMensaje('✅ Copiado al portapapeles — podés pegarlo en Excel');
    });
  };

  const mostrarMensaje = (msg) => {
    setMensaje(msg);
    setTimeout(() => setMensaje(""), 4000);
  };

  const toggleVacioFacturado = async (pedidoId, facturado) => {
    try {
      await axios.put(`https://comprasmercadomodelo.online/api/cierre/pedidos/${pedidoId}/vacio-facturado`,
        { facturado }, { headers: { authorization: token } }
      );
      cargarResumen(planSeleccionado);
    } catch (err) { console.error(err); }
  };
  const pedidosPorProveedor = pedidos.reduce((acc, p) => {
    const key = p.proveedor_nombre;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  const proveedoresOrdenados = Object.keys(pedidosPorProveedor).sort();
  const totalGeneral = pedidos.reduce((acc, p) => acc + (p.precio * (Number(p.cantidad_descargada_real) || Number(p.cantidad_cargada) || Number(p.cantidad_pedida) || 0)), 0);
  const planActual = planes.find(p => p.id === planSeleccionado);
  const planCerrado = planActual?.estado === 'cerrado';
  const planCargado = planActual?.estado === 'cargado';

  const conDiferencias = diferencias.filter(d =>
    Number(d.dif_carga) !== 0 ||
    Number(d.dif_descarga) !== 0 ||
    (d.observacion_descarga && d.observacion_descarga.trim() !== '')
  );
  const totalDiferencias = conDiferencias.length;
  const vaciosPersonalizados = pedidos.filter(p => p.cantidad_vacios !== null && p.cantidad_vacios !== undefined && p.cantidad_vacios !== '');

  const getDifColor = (dif) => {
    if (Number(dif) === 0) return '#27ae60';
    if (Math.abs(Number(dif)) <= 2) return '#f39c12';
    return '#e74c3c';
  };

  const getDifIcono = (dif) => {
    if (Number(dif) === 0) return '✅';
    if (Number(dif) > 0) return '⬆️';
    return '⬇️';
  };

  const getEstadoLabel = (estado) => {
    const map = { verde: '🟢 Verde', punto_medio: '🟡 Punto medio', maduro: '🟠 Maduro', demasiado_maduro: '🔴 Demasiado maduro' };
    return map[estado] || '-';
  };

  const getEstadoColor = (estadoCarga, estadoDescarga) => {
    if (!estadoCarga || !estadoDescarga) return null;
    const orden = ['verde', 'punto_medio', 'maduro', 'demasiado_maduro'];
    const idxCarga = orden.indexOf(estadoCarga);
    const idxDescarga = orden.indexOf(estadoDescarga);
    if (idxDescarga > idxCarga) return '#e74c3c';
    if (idxDescarga < idxCarga) return '#27ae60';
    return null;
  };

  if (planSeleccionado) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.botonVolver} onClick={() => setPlanSeleccionado(null)}>← Volver</button>
          <h3 style={styles.titulo}>✅ Cierre de Compras</h3>
          <div style={styles.headerBotones}>
            <input style={styles.inputNro} type="text" placeholder="Nro. Compra"
              value={nroCompra} onChange={(e) => setNroCompra(e.target.value)} />
            {planCargado && <span style={styles.badgeCargado}>📦 Carga lista — podés cerrar</span>}
            <button style={styles.botonCSV} onClick={exportarCSV}>📄 Exportar CSV</button>
            <button style={styles.botonCSV} onClick={exportarPlanillaDescarga}>Planilla de Descarga</button>
            {!planCerrado ? (
              <button style={{ ...styles.botonCerrar, backgroundColor: planCargado ? '#27ae60' : '#e74c3c', animation: planCargado ? 'pulse 1.5s infinite' : 'none' }}
                onClick={cerrarCompra}>
                {planCargado ? '✅ Cerrar Compra' : '🔒 Cerrar Compra'}
              </button>
            ) : (
              <span style={styles.cerradoBadge}>🔒 Cerrada</span>
            )}
          </div>
        </div>

        <style>{`@keyframes pulse { 0%{opacity:1} 50%{opacity:0.7} 100%{opacity:1} }`}</style>

        {mensaje && (
          <div style={styles.mensajeBox}>
            {mensaje}
            <button style={styles.cerrarMensaje} onClick={() => setMensaje('')}>✕</button>
          </div>
        )}

        <div style={{ ...styles.totalContainer, backgroundColor: planCerrado ? '#7f8c8d' : planCargado ? '#27ae60' : '#2c3e50' }}>
          <span style={styles.total}>💰 Total: ${totalGeneral.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
          <span style={styles.totalSub}>🏪 {proveedoresOrdenados.length} proveedores</span>
          {!planCerrado && !planCargado && <span style={styles.estadoCargaLabel}>⏳ Carga en progreso...</span>}
          {planCargado && <span style={styles.estadoCargaLabel}>📦 Carga completada</span>}
          {planCerrado && <span style={styles.estadoCargaLabel}>🔒 Compra cerrada</span>}
          {totalDiferencias > 0 && <span style={styles.alertaDif}>⚠️ {totalDiferencias} con diferencias/obs.</span>}
          {vaciosPersonalizados.length > 0 && <span style={styles.alertaVacios}>🧺 {vaciosPersonalizados.length} vacíos personalizados</span>}
          {productosNuevos.length > 0 && <span style={styles.alertaNuevos}>🆕 {productosNuevos.length} productos nuevos</span>}
        </div>

        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(vistaActiva === 'resumen' ? styles.tabActivo : {}) }}
            onClick={() => setVistaActiva('resumen')}>📋 Resumen por proveedor</button>
          <button style={{ ...styles.tab, ...(vistaActiva === 'diferencias' ? styles.tabActivo : {}), ...(totalDiferencias > 0 ? styles.tabAlerta : {}) }}
            onClick={() => setVistaActiva('diferencias')}>
            🔍 Diferencias/Obs. {totalDiferencias > 0 && `(${totalDiferencias})`}
          </button>
          <button style={{ ...styles.tab, ...(vistaActiva === 'vacios' ? styles.tabActivo : {}), ...(vaciosPersonalizados.length > 0 ? styles.tabVacios : {}) }}
            onClick={() => setVistaActiva('vacios')}>
            🧺 Vacíos Personalizados {vaciosPersonalizados.length > 0 && `(${vaciosPersonalizados.length})`}
          </button>
          <button style={{ ...styles.tab, ...(vistaActiva === 'unica' ? styles.tabActivo : {}), ...(productosNuevos.length > 0 ? styles.tabNuevos : {}) }}
            onClick={() => setVistaActiva('unica')}>
            📋 Lista Única {productosNuevos.length > 0 && `(${productosNuevos.length} nuevos)`}
          </button>
        </div>

        {vistaActiva === 'resumen' && (
          <div style={styles.listaProveedores}>
            {proveedoresOrdenados.map((nombreProveedor) => {
              const items = pedidosPorProveedor[nombreProveedor];
              const subtotalMerc = items.reduce((acc, p) => acc + (p.precio * (Number(p.cantidad_descargada_real) || Number(p.cantidad_cargada) || Number(p.cantidad_pedida) || 0)), 0);
              const subtotalVacios = items.reduce((acc, p) => acc + ((p.precio_vacio || 0) * (p.cantidad_pedida || 0)), 0);
              const total = subtotalMerc + subtotalVacios;
              return (
                <div key={nombreProveedor} style={styles.tarjetaProveedor}>
                  <div style={styles.headerProveedor}>
                    <div style={styles.proveedorIzq}>
                      <span style={styles.nombreProveedor}>{nombreProveedor}</span>
                      <span style={styles.codigoProveedor}>({items[0]?.proveedor_codigo})</span>
                    </div>
                    <div style={styles.subtotalesProveedor}>
                      <span style={styles.subtotalItem}>🛒 ${subtotalMerc.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                      {subtotalVacios > 0 && <span style={styles.subtotalItem}>🧺 ${subtotalVacios.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>}
                      <span style={styles.totalProveedor}>💰 ${total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  <table style={styles.tableEl}>
                    <thead>
                      <tr style={styles.thead}>
                        <th style={styles.th}>Código</th>
                        <th style={styles.th}>Producto</th>
                        <th style={styles.th}>Cant.</th>
                        <th style={styles.th}>Precio $</th>
                        <th style={styles.th}>Subtotal $</th>
                      <th style={styles.th}>Vacío</th>
                        <th style={styles.th}>$ Vacío</th>
                        <th style={styles.th}>Kg real</th>
                        <th style={styles.th}>Estado carga</th>
                        <th style={styles.th}>Estado descarga</th>
                        <th style={styles.th}>Obs.</th>
                        <th style={styles.th}>Actualizar precio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((p) => (
                        <tr key={p.id} style={{ ...styles.tr, backgroundColor: p.observacion_descarga ? '#fff9e6' : 'white' }}>
                          <td style={styles.tdCodigo}>{p.producto_codigo}</td>
                          <td style={styles.td}>{p.producto_nombre}</td>
                          <td style={styles.td}>{Number(p.cantidad_descargada_real) || Number(p.cantidad_cargada) || Number(p.cantidad_pedida) || 0}</td>
                          <td style={styles.td}>${Number(p.precio).toLocaleString('es-AR')}</td>
                          <td style={styles.td}>${(p.precio * (Number(p.cantidad_descargada_real) || Number(p.cantidad_cargada) || Number(p.cantidad_pedida) || 0)).toLocaleString('es-AR')}</td>
                          <td style={styles.td}>{p.vacio_nombre || '-'}</td>
                          <td style={styles.td}>{p.precio_vacio > 0 ? `$${Number(p.precio_vacio).toLocaleString('es-AR')}` : '-'}</td>
                          <td style={styles.td}>{p.kg_real > 0 ? `${p.kg_real} kg` : '-'}</td>
                          <td style={styles.td}><span style={styles.estadoBadge}>{getEstadoLabel(p.estado_mercaderia)}</span></td>
                          <td style={styles.td}><span style={styles.estadoBadge}>{getEstadoLabel(p.estado_mercaderia_descarga)}</span></td>
                          <td style={styles.td}>{p.observacion_descarga ? <span style={styles.obsResaltada}>⚠️ {p.observacion_descarga}</span> : '-'}</td>
                          <td style={styles.td}>
                            {!planCerrado && (
                              <div style={styles.inputGroup}>
                                <input style={styles.inputPrecio} type="number" placeholder={p.precio}
                                  value={preciosEditados[p.id] || ''}
                                  onChange={(e) => setPreciosEditados({ ...preciosEditados, [p.id]: e.target.value })} />
                                {preciosEditados[p.id] && (
                                  <button style={styles.botonActualizar} onClick={() => actualizarPrecio(p.id)}>✓</button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}

        {vistaActiva === 'diferencias' && (
          <div style={styles.tablaContainer}>
            {conDiferencias.length === 0 ? (
              <div style={styles.sinDiferencias}>✅ ¡Todo coincide! No hay diferencias ni observaciones.</div>
            ) : (
              <table style={styles.tableEl}>
                <thead>
                  <tr style={styles.thead}>
                    <th style={styles.th}>Código</th>
                    <th style={styles.th}>Producto</th>
                    <th style={styles.th}>Proveedor</th>
                    <th style={styles.th}>Comprado</th>
                    <th style={styles.th}>Cargado</th>
                    <th style={styles.th}>Descargado</th>
                    <th style={styles.th}>Dif. Carga</th>
                    <th style={styles.th}>Dif. Descarga</th>
                    <th style={styles.th}>Estado carga</th>
                    <th style={styles.th}>Estado descarga</th>
                    <th style={styles.th}>Obs. Descarga</th>
                    <th style={styles.th}>¿Avisado?</th>
                    <th style={styles.th}>Observación</th>
                    <th style={styles.th}>Guardar</th>
                  </tr>
                </thead>
                <tbody>
                  {conDiferencias.map((d) => {
                    const colorEstado = getEstadoColor(d.estado_carga, d.estado_descarga);
                    const tieneObsDescarga = d.observacion_descarga && d.observacion_descarga.trim() !== '';
                    const tieneDifCantidad = Number(d.dif_carga) !== 0 || Number(d.dif_descarga) !== 0;
                    return (
                      <tr key={d.id} style={{ ...styles.tr, backgroundColor: colorEstado ? '#fff5f5' : tieneObsDescarga && !tieneDifCantidad ? '#fff9e6' : 'white' }}>
                        <td style={styles.tdCodigo}>{d.producto_codigo}</td>
                        <td style={styles.td}>{d.producto_nombre}</td>
                        <td style={styles.td}>{d.proveedor_nombre}</td>
                        <td style={styles.tdCant}>{d.cantidad_pedida}</td>
                        <td style={styles.tdCant}>{d.cantidad_cargada || '-'}</td>
                        <td style={styles.tdCant}>{d.cantidad_descargada_real || '-'}</td>
                        <td style={styles.td}><span style={{ color: getDifColor(d.dif_carga), fontWeight: 'bold' }}>{getDifIcono(d.dif_carga)} {Number(d.dif_carga) > 0 ? `+${d.dif_carga}` : d.dif_carga}</span></td>
                        <td style={styles.td}><span style={{ color: getDifColor(d.dif_descarga), fontWeight: 'bold' }}>{getDifIcono(d.dif_descarga)} {Number(d.dif_descarga) > 0 ? `+${d.dif_descarga}` : d.dif_descarga}</span></td>
                        <td style={styles.td}><span style={styles.estadoBadge}>{getEstadoLabel(d.estado_carga)}</span></td>
                        <td style={styles.td}><span style={{ ...styles.estadoBadge, color: colorEstado || '#2c3e50' }}>{getEstadoLabel(d.estado_descarga)}{colorEstado === '#e74c3c' && ' ⬇️'}{colorEstado === '#27ae60' && ' ⬆️'}</span></td>
                        <td style={styles.td}>{tieneObsDescarga ? <span style={styles.obsResaltada}>⚠️ {d.observacion_descarga}</span> : '-'}</td>
                        <td style={styles.td}>
                          <label style={styles.checkAvisado}>
                            <input type="checkbox" checked={avisados[d.id] || false}
                              onChange={(e) => setAvisados({ ...avisados, [d.id]: e.target.checked })} />
                            &nbsp;{avisados[d.id] ? '✅ Sí' : '❌ No'}
                          </label>
                        </td>
                        <td style={styles.td}>
                          <input style={styles.inputObs} type="text" placeholder="Cómo se resolvió..."
                            value={observaciones[d.id] || ''}
                            onChange={(e) => setObservaciones({ ...observaciones, [d.id]: e.target.value })} />
                        </td>
                        <td style={styles.td}>
                          <button style={styles.botonGuardarDif} onClick={() => guardarDiferencia(d.id)}>💾</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {vistaActiva === 'vacios' && (
          <div style={styles.tablaContainer}>
            {vaciosPersonalizados.length === 0 ? (
              <div style={styles.sinDiferencias}>✅ No hay vacíos personalizados en este plan.</div>
            ) : (
              <>
                <div style={styles.resumenVaciosHeader}>
                  <span style={styles.resumenVaciosTotal}>🧺 Total vacíos personalizados: <strong>${vaciosPersonalizados.reduce((acc, p) => acc + (Number(p.precio_vacio) * Number(p.cantidad_vacios)), 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong></span>
                  <span style={styles.resumenVaciosCount}>{vaciosPersonalizados.length} productos</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "15px" }}>
                  {Object.entries(vaciosPersonalizados.reduce((acc, p) => {
                    const key = p.proveedor_nombre;
                    if (!acc[key]) acc[key] = 0;
                    acc[key] += Number(p.precio_vacio) * Number(p.cantidad_vacios);
                    return acc;
                  }, {})).map(([nombreProv, subtotal]) => (
                    <span key={nombreProv} style={{ backgroundColor: "white", padding: "6px 12px", borderRadius: "6px", fontSize: "13px", border: "1px solid #f0e6c8" }}>
                      <strong>{nombreProv}:</strong> ${subtotal.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                    </span>
                  ))}
                </div>
                <table style={styles.tableEl}>
                  <thead>
                    <tr style={styles.thead}>
                      <th style={styles.th}>Proveedor</th>
                      <th style={styles.th}>Producto</th>
                      <th style={styles.th}>Código Vacío</th>
                      <th style={styles.th}>Vacío</th>
                      <th style={styles.th}>Cant. Vacíos</th>
                      <th style={styles.th}>Precio Vacío $</th>
                      <th style={styles.th}>Subtotal $</th>
                      <th style={styles.th}>Facturado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vaciosPersonalizados.map(p => (
                      <tr key={p.id} style={{ ...styles.tr, backgroundColor: '#fffbea' }}>
                        <td style={styles.td}>{p.proveedor_nombre}</td>
                        <td style={styles.td}>{p.producto_nombre}</td>
                        <td style={styles.td}>{p.vacio_codigo || '-'}</td>
                        <td style={styles.td}><span style={styles.vacioBadge}>{p.vacio_nombre || '-'}</span></td>
                        <td style={styles.tdCant}>{p.cantidad_vacios}</td>
                        <td style={styles.td}>${Number(p.precio_vacio).toLocaleString('es-AR')}</td>
                        <td style={{ ...styles.td, fontWeight: 'bold', color: '#27ae60' }}>${(Number(p.precio_vacio) * Number(p.cantidad_vacios)).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                        <td style={styles.td}>
                          <input type="checkbox" checked={!!p.vacio_facturado}
                            onChange={(e) => toggleVacioFacturado(p.id, e.target.checked)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {vistaActiva === 'unica' && (
          <div style={styles.tablaContainer}>
            <div style={styles.unicaHeader}>
              <div style={styles.unicaIzq}>
                <span style={styles.unicaTitulo}>📋 Productos nuevos para agregar a la Lista Única</span>
                <span style={styles.unicaSubtitulo}>Estos productos no estaban en la lista anterior</span>
              </div>
              <div style={styles.unicaDer}>
                <input type="file" accept=".xlsx,.xls" ref={fileInputRef} style={{ display: 'none' }}
                  onChange={subirListaUnica} />
                <button style={styles.botonSubirUnica} onClick={() => fileInputRef.current.click()}
                  disabled={subiendoUnica}>
                  {subiendoUnica ? '⏳ Cargando...' : '📂 Subir lista anterior'}
                </button>
                {productosNuevos.length > 0 && (
                  <button style={styles.botonCopiarUnica} onClick={copiarListaUnica}>
                    📋 Copiar para Excel ({productosNuevos.length})
                  </button>
                )}
              </div>
            </div>
            {!unicaCargada ? (
              <div style={styles.sinDiferencias}>📂 Subí la lista única anterior para ver los productos nuevos</div>
            ) : productosNuevos.length === 0 ? (
              <div style={styles.sinDiferencias}>✅ No hay productos nuevos — todos están en la lista única anterior</div>
            ) : (
              <table style={styles.tableEl}>
                <thead>
                  <tr style={styles.thead}>
                    <th style={styles.th}>Código</th>
                    <th style={styles.th}>Nombre</th>
                    <th style={styles.th}>Precio vta $</th>
                    <th style={styles.th}></th>
                    <th style={styles.th}>Costo $</th>
                  </tr>
                </thead>
                <tbody>
                  {productosNuevos.map((p, i) => (
                    <tr key={i} style={{ ...styles.tr, fontWeight: 'bold' }}>
                      <td style={{ ...styles.tdCodigo, fontWeight: 'bold' }}>{p.codigo_barras}</td>
                      <td style={{ ...styles.td, fontWeight: 'bold' }}>{p.nombre.toLowerCase()}</td>
                      <td style={styles.td}></td>
                      <td style={styles.td}></td>
                      <td style={{ ...styles.td, fontWeight: 'bold', color: '#2c3e50' }}>{Math.round(Number(p.costo))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.titulo}>✅ Cierre de Compras</h3>
      <p style={styles.subtitulo}>Seleccioná el plan a cerrar</p>
      <div style={styles.lista}>
        {planes.length === 0 ? (
          <p style={styles.sinPlanes}>No hay planes listos para cerrar</p>
        ) : (
          planes.map((plan) => {
            const esCargado = plan.estado === 'cargado';
            const esCerrado = plan.estado === 'cerrado';
            return (
              <div key={plan.id} style={{ ...styles.tarjeta, borderLeft: esCargado ? '4px solid #27ae60' : esCerrado ? '4px solid #7f8c8d' : '4px solid #3498db' }}
                onClick={() => cargarResumen(plan.id)}>
                <div style={styles.tarjetaInfo}>
                  <span style={styles.fecha}>📅 {new Date(plan.fecha).toLocaleDateString("es-AR", { timeZone: "UTC" })}</span>
                  <span style={styles.comentario}>{plan.comentario || 'Sin comentarios'}</span>
                </div>
                <span style={{ ...styles.estadoPlan, backgroundColor: esCerrado ? '#7f8c8d' : esCargado ? '#27ae60' : '#3498db' }}>
                  {esCargado ? '📦 Listo para cerrar' : esCerrado ? '🔒 Cerrado' : plan.estado}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { padding: '20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' },
  titulo: { color: '#2c3e50', fontSize: '22px', margin: 0 },
  subtitulo: { color: '#7f8c8d', marginBottom: '20px' },
  headerBotones: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  inputNro: { padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', width: '130px' },
  botonVolver: { backgroundColor: '#95a5a6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' },
  botonCSV: { backgroundColor: '#2980b9', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' },
  botonCerrar: { color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
  cerradoBadge: { backgroundColor: '#7f8c8d', color: 'white', padding: '10px 20px', borderRadius: '6px', fontSize: '14px' },
  badgeCargado: { backgroundColor: '#eafaf1', color: '#27ae60', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', border: '1px solid #27ae60' },
  mensajeBox: { backgroundColor: '#ffeaa7', padding: '15px 20px', borderRadius: '8px', marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' },
  cerrarMensaje: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' },
  totalContainer: { padding: '15px 20px', borderRadius: '8px', marginBottom: '15px', display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap', transition: 'background-color 0.3s' },
  total: { color: 'white', fontSize: '18px', fontWeight: 'bold' },
  totalSub: { color: 'rgba(255,255,255,0.8)', fontSize: '14px' },
  estadoCargaLabel: { color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 'bold' },
  alertaDif: { backgroundColor: '#e74c3c', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '13px' },
  alertaVacios: { backgroundColor: '#f39c12', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '13px' },
  alertaNuevos: { backgroundColor: '#8e44ad', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '13px' },
  tabs: { display: 'flex', gap: '5px', marginBottom: '15px', flexWrap: 'wrap' },
  tab: { padding: '10px 20px', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px' },
  tabActivo: { backgroundColor: '#2c3e50', color: 'white', border: '1px solid #2c3e50' },
  tabAlerta: { borderColor: '#e74c3c', color: '#e74c3c' },
  tabVacios: { borderColor: '#f39c12', color: '#f39c12' },
  tabNuevos: { borderColor: '#8e44ad', color: '#8e44ad' },
  listaProveedores: { display: 'flex', flexDirection: 'column', gap: '15px' },
  tarjetaProveedor: { backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', overflow: 'hidden' },
  headerProveedor: { backgroundColor: '#2c3e50', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' },
  proveedorIzq: { display: 'flex', alignItems: 'center', gap: '10px' },
  nombreProveedor: { color: 'white', fontWeight: 'bold', fontSize: '15px' },
  codigoProveedor: { color: 'rgba(255,255,255,0.6)', fontSize: '13px' },
  subtotalesProveedor: { display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' },
  subtotalItem: { color: 'rgba(255,255,255,0.8)', fontSize: '13px' },
  totalProveedor: { color: '#2ecc71', fontSize: '15px', fontWeight: 'bold' },
  tablaContainer: { backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', overflow: 'auto' },
  sinDiferencias: { padding: '40px', textAlign: 'center', color: '#27ae60', fontSize: '18px', fontWeight: 'bold' },
  resumenVaciosHeader: { padding: '15px 20px', backgroundColor: '#fef9e7', borderBottom: '1px solid #f39c12', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  resumenVaciosTotal: { fontSize: '15px', color: '#2c3e50' },
  resumenVaciosCount: { fontSize: '13px', color: '#7f8c8d' },
  vacioBadge: { backgroundColor: '#eaf2ff', color: '#2980b9', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' },
  unicaHeader: { padding: '15px 20px', backgroundColor: '#f3eaff', borderBottom: '1px solid #8e44ad', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' },
  unicaIzq: { display: 'flex', flexDirection: 'column', gap: '3px' },
  unicaTitulo: { fontSize: '15px', color: '#2c3e50', fontWeight: 'bold' },
  unicaSubtitulo: { fontSize: '12px', color: '#7f8c8d' },
  unicaDer: { display: 'flex', gap: '10px', alignItems: 'center' },
  botonSubirUnica: { backgroundColor: '#8e44ad', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  botonCopiarUnica: { backgroundColor: '#27ae60', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  tableEl: { width: '100%', borderCollapse: 'collapse' },
  thead: { backgroundColor: '#f8f9fa' },
  th: { color: '#7f8c8d', padding: '10px 15px', textAlign: 'left', fontSize: '12px', borderBottom: '1px solid #ecf0f1', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #ecf0f1' },
  td: { padding: '10px 15px', fontSize: '13px', color: '#2c3e50' },
  tdCodigo: { padding: '10px 15px', fontSize: '12px', color: '#3498db', fontWeight: 'bold' },
  tdCant: { padding: '10px 15px', fontSize: '14px', fontWeight: 'bold', color: '#2c3e50', textAlign: 'center' },
  inputGroup: { display: 'flex', gap: '8px', alignItems: 'center' },
  inputPrecio: { width: '90px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' },
  inputObs: { width: '180px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' },
  botonActualizar: { backgroundColor: '#27ae60', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' },
  botonGuardarDif: { backgroundColor: '#2980b9', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' },
  obsResaltada: { color: '#e74c3c', fontWeight: 'bold', fontSize: '12px' },
  estadoBadge: { fontSize: '12px' },
  checkAvisado: { display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  lista: { display: 'flex', flexDirection: 'column', gap: '15px' },
  sinPlanes: { textAlign: 'center', color: '#95a5a6', padding: '40px' },
  tarjeta: { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  tarjetaInfo: { display: 'flex', flexDirection: 'column', gap: '5px' },
  fecha: { fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' },
  comentario: { color: '#7f8c8d' },
  estadoPlan: { color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }
};

export default Cierre;
